import type { Partner } from '@tagadapay/crm-entities';

import {
  brandingConfig,
  isPartnerHost,
  isWhitelabel,
  usesDefaultBrand,
  type BrandConfig,
} from '@/config/branding';
import { neutralBrand } from '@/config/brands';
import type { ResellerPartnerConfig } from '@/config/reseller-partners';
import { mergeCodeResellerBrand } from '@/lib/branding/merge-code-reseller-brand';
import { mergePartnerBranding } from '@/lib/branding/merge-partner-branding';

export type ResolveOptions = {
  /** Whether the current host's DB branding overlay may be merged. */
  usesHostDbOverlay: boolean;
  hasOrgPartnerSlug: boolean;
  /**
   * Whether the merge base must be neutral (no TagadaPay defaults) — true for
   * DB-branded partner hosts and the reseller-slug context, so an unfilled field
   * renders blank/neutral instead of leaking our brand. False for code-branded
   * partner hosts (their own brand file is the safe base) and the primary.
   */
  neutralBase: boolean;
};

const shouldMergeDb = (dbBranding: Partner.PartnerBranding | null, options: ResolveOptions): boolean =>
  !!dbBranding && (options.usesHostDbOverlay || options.hasOrgPartnerSlug);

/** Layer code reseller + DB overlay onto the (possibly neutralized) base brand config. */
export const resolveEffectiveBrand = (
  base: BrandConfig,
  dbBranding: Partner.PartnerBranding | null,
  codeReseller: ResellerPartnerConfig | null,
  options: ResolveOptions,
): BrandConfig => {
  const effectiveBase = options.neutralBase ? neutralBrand : base;
  const withCode = codeReseller ? mergeCodeResellerBrand(effectiveBase, codeReseller) : effectiveBase;
  return shouldMergeDb(dbBranding, options) ? mergePartnerBranding(withCode, dbBranding!) : withCode;
};

/**
 * Pure mapping from host/session facts to resolve options — the heart of the
 * Fix #1 (neutral base) + Fix #2 (gate-flip) gating. Extracted so the gating can
 * be unit tested without a real browser host.
 */
export const computeResolveOptions = (input: {
  usesDefaultBrand: boolean;
  isPartnerHost: boolean;
  isWhitelabel: boolean;
  orgSlug: string | null;
}): ResolveOptions => ({
  // Fix #2 (gate-flip): the host DB overlay applies to ANY partner host, so a
  // filled DB record wins field-by-field even for code-branded partners.
  usesHostDbOverlay: input.isPartnerHost,
  hasOrgPartnerSlug: !!input.orgSlug,
  // Fix #1: neutralize the base for DB-branded partner hosts and for the
  // reseller-slug context on the primary domain — never for a code-branded
  // partner host (keep its code brand), the primary, nor a whitelabel deploy
  // (an org carrying a whitelabel slug must not neutralize the whitelabel brand).
  neutralBase: input.usesDefaultBrand || (!input.isWhitelabel && !input.isPartnerHost && !!input.orgSlug),
});

/** Default ambient resolution for the current deploy host. */
export const resolveAmbientBrand = (
  dbBranding: Partner.PartnerBranding | null,
  codeReseller: ResellerPartnerConfig | null,
  orgSlug: string | null,
): BrandConfig =>
  resolveEffectiveBrand(
    brandingConfig,
    dbBranding,
    codeReseller,
    computeResolveOptions({ usesDefaultBrand, isPartnerHost, isWhitelabel, orgSlug }),
  );
